export const DEFAULT_AVATAR = "/img/user_avatar.jpg";
export const DEFAULT_BELT_PHOTO = "/img/belt/ceinture_judo_blanche.png";

export const DEFAULT_BELT_FORM = {
  belt_id: "",
  date_passage: "",
};

export const DEFAULT_FILTERS = {
  search: "",
  groupId: "",
  beltId: "",
};

export const DEFAULT_BULK_BELT_FORM = {
  belt_id: "",
  date_passage: "",
};

export function safeDate(value) {
  if (!value) return null;

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatGradeDate(value) {
  const date = safeDate(value);

  if (!date) return "Date non renseignée";

  return new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
}

export function dateToInputValue(value) {
  if (!value) return "";

  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    return value.slice(0, 10);
  }

  const date = safeDate(value);

  if (!date) return "";

  return date.toISOString().slice(0, 10);
}

export function getUserId(user) {
  return user?.userId || user?.id || null;
}

export function getAdherentName(adherent) {
  return (
    [adherent?.prenom, adherent?.nom].filter(Boolean).join(" ") || "Judoka"
  );
}

export function getAdherentPhoto(adherent) {
  return adherent?.photo_url || DEFAULT_AVATAR;
}

export function getGroupNames(row) {
  if (!row?.groups?.length) return "Sans groupe";

  return row.groups.map((group) => group.nom).filter(Boolean).join(", ");
}

export function getBeltName(grade) {
  return grade?.BeltColor?.nom || grade?.belt_name || "Ceinture";
}

export function getBeltId(grade) {
  return grade?.belt_id || grade?.BeltColor?.id || "";
}

export function getBeltImage(grade) {
  const beltUrl = grade?.BeltColor?.belt_url;

  if (!beltUrl) return DEFAULT_BELT_PHOTO;

  return `/img/belt/${beltUrl}`;
}

export function getBeltImageFromBelt(belt) {
  if (!belt?.belt_url) return DEFAULT_BELT_PHOTO;

  return `/img/belt/${belt.belt_url}`;
}

export function sortBeltHistory(grades = []) {
  return [...grades].sort((a, b) => {
    const dateA = safeDate(a?.date_passage)?.getTime() || 0;
    const dateB = safeDate(b?.date_passage)?.getTime() || 0;

    return dateB - dateA;
  });
}

export function getLastGrade(grades = []) {
  const sorted = sortBeltHistory(grades);
  return sorted.length > 0 ? sorted[0] : null;
}

export function mergeApprovedAdherentsById(adherents = []) {
  const map = new Map();

  adherents.forEach((adherent) => {
    if (!adherent?.id) return;

    const existing = map.get(adherent.id);

    if (!existing) {
      map.set(adherent.id, {
        ...adherent,
        groups: adherent.group ? [adherent.group] : [],
      });
      return;
    }

    const alreadyHasGroup = existing.groups.some(
      (group) => Number(group.id) === Number(adherent.group?.id),
    );

    map.set(adherent.id, {
      ...existing,
      ...adherent,
      groups:
        adherent.group && !alreadyHasGroup
          ? [...existing.groups, adherent.group]
          : existing.groups,
    });
  });

  return Array.from(map.values()).sort((a, b) => {
    const nameA = getAdherentName(a);
    const nameB = getAdherentName(b);

    return nameA.localeCompare(nameB, "fr");
  });
}

export function buildGradeRows(adherents = [], historiesByAdherent = {}) {
  return adherents.map((adherent) => {
    const grades = sortBeltHistory(historiesByAdherent[adherent.id] || []);
    const lastGrade = getLastGrade(grades);

    return {
      adherent,
      groups: adherent.groups || [],
      grades,
      lastGrade,
      totalGrades: grades.length,
    };
  });
}

export function filterGradeRows(rows = [], filters = {}) {
  const search = String(filters.search || "").trim().toLowerCase();
  const groupId = String(filters.groupId || "");
  const beltId = String(filters.beltId || "");

  return rows.filter((row) => {
    const adherentName = getAdherentName(row.adherent).toLowerCase();
    const groupNames = getGroupNames(row).toLowerCase();

    const matchesSearch =
      !search ||
      adherentName.includes(search) ||
      groupNames.includes(search) ||
      String(row.adherent?.club || "").toLowerCase().includes(search);

    const matchesGroup =
      !groupId ||
      row.groups.some((group) => String(group.id) === String(groupId));

    const currentBeltId = row.lastGrade ? String(getBeltId(row.lastGrade)) : "";

    const matchesBelt = !beltId || currentBeltId === beltId;

    return matchesSearch && matchesGroup && matchesBelt;
  });
}

export function buildCoachGradeStats(rows = []) {
  const withBelt = rows.filter((row) => row.lastGrade).length;
  const withoutBelt = rows.length - withBelt;

  const totalGrades = rows.reduce((acc, row) => acc + row.totalGrades, 0);

  return {
    totalJudokas: rows.length,
    withBelt,
    withoutBelt,
    totalGrades,
  };
}

export function normalizeBeltPayload(form, adherentId) {
  return {
    adherent_id: adherentId,
    belt_id: form.belt_id,
    date_passage: form.date_passage,
  };
}

export function validateBeltForm(form) {
  if (!form.belt_id) {
    return "Sélectionnez une ceinture.";
  }

  if (!form.date_passage) {
    return "Sélectionnez une date de passage.";
  }

  const date = safeDate(form.date_passage);

  if (!date) {
    return "La date de passage est invalide.";
  }

  return "";
}